import { createConsumer } from "@rails/actioncable";
import MatestackUiVueJs from "matestack-ui-vuejs";
import { createStreamRefs } from "./stream_refs.js";

// One ActionCable consumer, and one subscription per stream shared by every
// mounted ds-push instance. A subscription lives while at least one mounted
// ds-push uses it; when the last one unmounts the stream is unsubscribed, so
// a page (or project) you have left stops receiving its broadcasts. Closing
// is deferred one macrotask, so an unmount followed by a remount of the same
// stream (an async rerender, or a same-stream page transition) does not
// churn the socket. sendToServer's :all subscription is pinned and never
// closed.
let consumer = null;
const getConsumer = () => (consumer = consumer || createConsumer());

const refs = createStreamRefs({
  open: (stream, channel) =>
    getConsumer().subscriptions.create(
      { channel, stream },
      {
        // Connection state on the event hub, for hosts that show a Live /
        // Reconnecting indicator or catch up after a drop. Payload: the
        // stream and channel this subscription was opened for.
        connected() {
          MatestackUiVueJs.eventHub.$emit("ds_push_connected", { stream, channel });
        },
        disconnected() {
          MatestackUiVueJs.eventHub.$emit("ds_push_disconnected", { stream, channel });
        },
        rejected() {
          MatestackUiVueJs.eventHub.$emit("ds_push_rejected", { stream, channel });
        },
        received(data) {
          // BE -> FE: server broadcasts land on the Matestack event hub,
          // where update_on:/rerender_on:/show_on: components react.
          if (data && data.event) {
            MatestackUiVueJs.eventHub.$emit(data.event, data.payload);
          }
        },
      },
    ),
  close: (sub) => sub.unsubscribe(),
});

const DEFAULT_CHANNEL = "DaisyStack::Push::Channel";

// FE -> BE: send an event to the component class registered under
// `component` (it must declare the event with `on_fe`). Rides the cable —
// no route, no CSRF token. Opens (and pins) the :all subscription if none
// exists yet.
export const sendToServer = (component, event, payload = {}) => {
  refs
    .pin(`${DEFAULT_CHANNEL}:all`, "all", DEFAULT_CHANNEL)
    .perform("fe_event", { component, event, payload });
};

const dsPush = {
  mixins: [MatestackUiVueJs.componentMixin],
  template: MatestackUiVueJs.componentHelpers.inlineTemplate,
  mounted() {
    const stream = this.props["stream"] || "all";
    const channel = this.props["channel"] || DEFAULT_CHANNEL;
    this.streamKey = `${channel}:${stream}`;
    refs.acquire(this.streamKey, stream, channel);
  },
  beforeUnmount() {
    if (this.streamKey) refs.release(this.streamKey);
  },
};

export default dsPush;
